/**
 * ⛏️ 矿床与硫化物共生鉴定增强模块 (Sulfide & Ore Paragenesis Module)
 * 目标：解决大模型对“黑不拉几”和“亮闪闪”金属矿石的“脸盲”问题，
 * 强制引入“矿物共生(Paragenesis)”和“结构解剖”逻辑。
 */
export const SULFIDE_LOGIC = {
  oreRules: `
  === ⛏️ 多金属与硫化物鉴定高级法则 (v8.1.0 专家级) ===
  **核心指令**: 当用户上传的图片呈现【金属光泽】、【黑灰沉重感】或【金黄色斑点】时，强制触发本协议。
  绝对禁止仅凭颜色（如黄色）草率判定为自然金或黄铁矿，必须执行以下结构解剖逻辑。

  [1. 结构剥离映射定则 (Structural Mapping Logic)]
  在你心中必须将图片分为两部分独立分析：
  - **基底/脉石 (Gangue)**：如白色的石英脉、暗绿色的绿泥石或方解石。不要将脉石与金属矿体混为一谈。
  - **金属矿体 (Ore Minerals)**：识别它们的产出状态（是网脉状交叉、浸染状星点、还是致密块状？）。

  [2. 硫化物与自然元素“A vs B”强力对抗库 (Definitive Optical Anchors)]
  - **自然金 (Native Gold) 极其严格的视觉签锚点 (参考港大标本)**：
    - **严禁**将黄铁矿或黄铜矿直接误认为自然金。
    - **形态 (Habit)**：自然金绝不呈现完美的几何多面体晶形（如立方体）。它必须是【不规则的树枝状 (Dendritic)】、【海绵状】、【鳞片状】或【致密块状】。
    - **共生特写**：真正的自然金标本中，金金属往往如“浇铸”般【镶嵌或包裹在白色的石英基底 (Quartz Matrix)】中，呈现极强的延展性外观，边缘圆滑而非锐利的解理面。
    - **颜色与光泽**：纯正浓郁的【金黄色 (Golden Yellow)】，呈现顶级的【金属光泽】。哪怕在光线暗处也不会发绿或发灰。

  - **自然银 (Native Silver) vs 其他银灰色矿物**：
    - **形态判定**：真正的自然银常以极具特征的【弯曲丝状 (Wire-like)】、【树枝状】或【扭曲的薄片状】产出，宛如一团乱铁丝或凝固的钢水。绝对不会呈现平整解理面。
    - **颜色与氧化 (Tarnish)**：新鲜面为银白色（金属光泽极强），但标本表面几乎总是因氧化而带有【灰黑色至黑色的表面锖色/氧化膜】。如果看到银白带黑的扭曲丝状物，优先考虑自然银。

  - **约瑟夫石 / 镍铁矿 (Awaruite) 的极性特征**：
    - **形态与触感表现**：这是一种极其罕见的陆地原生镍铁合金（往往与蛇纹岩共生）。颜色呈强烈的【银白色至锡白色】，具有耀眼的金属光泽。
    - **关键锚点**：它通常呈现【水滴状】、【不规则圆滑卵石状】或略带压扁的致密块状。表面十分圆溜平滑，像是一块被熔化后凝固的银疙瘩，【没有棱角也没有解理】。通常具有强磁性（需在 diagnosticTests 中建议磁铁测试）。

  - **自然铋 (Native Bismuth) 的微特征识别**：
    - **颜色特征**：第一眼看上去是银白色到浅铅灰色，但其极其隐蔽的致命特征是表面常常带有一层【微弱的玫瑰红色/红黄色小晕彩】。
    - **形态特征**：虽然多为致密块状，但当你仔细观察其断口，常常能看到隐约的【羽毛状】或【树枝状】的结晶纹理。

  - **自然汞 (Native Mercury) 专项排雷**：
    - **形态绝杀法则**：汞是常温下唯一的液态金属。如果一整块看起来是固体的岩石，它【绝对不可能是】自然汞。
    - **共生关联**：自然汞在标本上的表现，永远附着在其它岩石（特别是红色的【辰砂/朱砂 Cinnabar】）表面的孔缝中，表现为【极其细小的、银白色的完美液态微小水滴 (Globules)】。如果你在暗红色岩石的凹坑里看到了闪亮的银色极小圆珠，那就是自然汞。

  - **黄铁矿 (Pyrite) vs 自然金 (Gold)**：
    - 若看到完好的【立方体、五角十二面体】或带有平行晶面的条纹，颜色为【浅黄铜色 (Brass Yellow)】（有时微带绿调），必须强制判定为黄铁矿（愚人金），绝不是自然金。
  - **黄铁矿 (Pyrite) vs 黄铜矿 (Chalcopyrite)**：
    - 颜色与晶形判断：黄铜矿更偏【深黄铜绿色】，且极少出现完好晶形（多为他形致密块状）。若看见极其方正立体的金色方块，强制判定为黄铁矿。
  - **斑铜矿 (Bornite) 专项防漏：**
    - 寻找【锖色/晕彩】。若黄铜色表面出现紫、蓝、红交织的斑点（像孔雀羽毛或汽油膜），必须优先考虑斑铜矿（即“孔雀石”原矿），不得笼统称为黄铜矿。
  - **闪锌矿 (Sphalerite) vs 极性矿物：**
    - 若看到棕黑色至铁黑色，具强烈的【树脂光泽或半金属光泽】（像琥珀或松香表面），不应判定为普通暗色岩石，应判定为闪锌矿。

  [3. 铅锌铜共生铁律 (Paragenetic Associations)]
  地质学定律：硫化物往往不是孤立存在的。
  - **定律 A (铅锌不分家)**：如果你（AI）在图中看到了阶梯状解理、铅灰色的【方铅矿 (Galena)】，你必须极其仔细地在它旁边寻找棕黑色的【闪锌矿】和金色的【黄铜矿】踪迹。
  - **定律 B (黄铁-石英共生)**：如果你确认了基底是白色不透明的【石英 (Quartz)】，且上面附着有金色矿物，这是经典的含金/含多金属石英脉，请在报告的地质背景中着重描述这种热液脉状成矿环境。

  [4. 诊断测试降维要求]
  若置信度 < 88%，在 diagnosticTests 中必须输出以下硬核物理测试：
  - 测试1：条痕测试 (Streak Test)。要求用户在无釉白瓷板上划擦（黄铁矿为绿黑色，自然金为金黄色，赤铁矿为樱红色）。
  - 测试2：小刀硬度测试。要求用户用小刀刻划（黄铜矿/自然金小刀可刻穿，黄铁矿刻不动）。
  `
};
